import React, { useEffect, useState } from "react";
import UserScreenWindow from "../User/Components/UserScreenWindow";
import { useSelector } from "react-redux";
import {
  getAllDocsSnap,
  queryAllDocsByFieldSnap,
} from "../../utils/firebase/Firebase.utils";
import {
  FiPackage,
  FiCalendar,
  FiSearch,
  FiUser,
  FiClock,
  FiXCircle,
  FiCheckCircle,
  FiArrowRight,
  FiAlertCircle,
  FiTrendingUp,
  FiTrendingDown,
  FiFileText,
  FiTag,
  FiBox,
} from "react-icons/fi";
import MobileWrapper from "../../components/MobileWrapper";

function InventoryIssuances() {
  const currentUser = useSelector((state) => state.userObject);
  const [issuances, setIssuances] = useState([]);
  const [filteredIssuances, setFilteredIssuances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  // Colors based on requirements
  const primaryColor = "#363062";
  const secondaryColor = "#8FCD00";

  useEffect(() => {
    const fetchIssuances = async () => {
      // Query all issuances where the current user is listed in issuedToUsers array
      await getAllDocsSnap("inventoryTransactions", (data) => {
        // Filter transactions for issuances only and where current user is involved
        const userIssuances = data.filter((transaction) => {
          // Check if it's an issuance and related to the current user
          if (transaction.transactionType !== "issuance") return false;

          // // If user is the one who created the issuance
          // if (transaction.userEmail === currentUser.email) return true;

          // If user is one of the recipients
          if (
            transaction.issuedToUsers &&
            transaction.issuedToUsers.some(
              (user) => user.value === currentUser.email
            )
          ) {
            return true;
          }

          // Check if the user's name or email is mentioned in issuedTo field
          if (
            transaction.issuedTo &&
            (transaction.issuedTo.includes(currentUser.displayName) ||
              transaction.issuedTo.includes(currentUser.email))
          ) {
            return true;
          }

          return false;
        });

        // Sort by date, newest first
        userIssuances.sort((a, b) => {
          const dateA = new Date(a.date || a.createdAt).getTime();
          const dateB = new Date(b.date || b.createdAt).getTime();
          return dateB - dateA;
        });

        setIssuances(userIssuances);
        setFilteredIssuances(userIssuances);
        setLoading(false);
      });
    };

    fetchIssuances();
  }, [currentUser.email, currentUser.displayName]);

  // Apply search filter
  useEffect(() => {
    let filtered = [...issuances];

    // Apply search term filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((issuance) => {
        // Search in item name
        if (issuance.itemName?.toLowerCase().includes(term)) return true;

        // Search in issued to
        if (issuance.issuedTo?.toLowerCase().includes(term)) return true;

        // Search in purpose
        if (issuance.purpose?.toLowerCase().includes(term)) return true;

        // Search in remarks
        if (issuance.remarks?.toLowerCase().includes(term)) return true;

        return false;
      });
    }

    setFilteredIssuances(filtered);
  }, [searchTerm, issuances]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCardClick = (id) => {
    // Navigate to details page (to be implemented)
    window.location.href = `/inventory-issuance/${id}`;
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <MobileWrapper label="Inventory Issuances">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div
              className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2"
              style={{ borderColor: primaryColor }}
            ></div>
            <p className="mt-2 text-sm" style={{ color: primaryColor }}>
              Loading issuance records...
            </p>
          </div>
        </div>
      </MobileWrapper>
    );
  }

  return (
    <MobileWrapper label="Inventory Issuances">
      <div className="w-full mx-auto px-4 max-w-4xl">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-6 bg-[#363062]/5 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FiBox className="text-white text-base" />
            </div>
            <div>
              <h1 className="text-sm font-medium text-[#363062]">
                Inventory Issuances
              </h1>
              <p className="text-xs text-gray-500">
                View and manage inventory items assigned to you
              </p>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <FiSearch className="w-3.5 h-3.5 text-gray-500" />
          </div>
          <input
            type="text"
            className="block w-full p-2.5 pl-10 text-xs border rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
            placeholder="Search items, purposes, or remarks..."
            value={searchTerm}
            onChange={handleSearch}
            style={{ borderColor: "#E2E8F0" }}
          />
          {searchTerm && (
            <button
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setSearchTerm("")}
            >
              <FiXCircle className="w-3.5 h-3.5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Issuances Cards */}
        <div className="mt-4 space-y-3">
          {filteredIssuances.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
              <div className="p-3 bg-[#363062]/10 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <FiPackage className="text-[#363062] text-lg" />
              </div>
              <h3 className="text-sm font-medium text-[#363062] mb-1">
                {issuances.length === 0
                  ? "No Issuance Records"
                  : "No Matching Results"}
              </h3>
              <p className="text-xs text-gray-500">
                {issuances.length === 0
                  ? "You have no inventory items issued to you"
                  : "Try adjusting your search terms"}
              </p>
            </div>
          ) : (
            filteredIssuances.map((issuance) => (
              <div
                key={issuance.id}
                className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md hover:translate-y-[-2px] cursor-pointer"
                // onClick={() => handleCardClick(issuance.id)}
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-[#363062]">
                      {issuance.itemName || "Unknown Item"}
                    </div>
                    <div
                      className="flex items-center px-2 py-1 rounded-full text-xs font-medium gap-1.5"
                      style={{
                        backgroundColor: `${secondaryColor}15`,
                        color: secondaryColor,
                      }}
                    >
                      <span className="text-xs">
                        <FiPackage />
                      </span>
                      {issuance.quantity} units
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                    <div className="flex items-center gap-1.5">
                      <FiCalendar className="text-gray-400 flex-shrink-0" />
                      <span>
                        {formatDate(issuance.date || issuance.createdAt)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FiUser className="text-gray-400 flex-shrink-0" />
                      <span>{issuance.user || "Unknown"}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FiTag className="text-gray-400 flex-shrink-0" />
                      <span>{issuance.purpose || "No purpose specified"}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FiFileText className="text-gray-400 flex-shrink-0" />
                      <span>
                        {issuance.issuedTo || "No recipient specified"}
                      </span>
                    </div>
                  </div>

                  {/* Remarks Section (if any) */}
                  {issuance.remarks && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <div className="text-xs text-gray-500 mb-1.5">
                        Remarks:
                      </div>
                      <div className="text-xs text-gray-600">
                        {issuance.remarks}
                      </div>
                    </div>
                  )}
                </div>
                {/* Footer with View Details */}
                {/* <div className="flex justify-end items-center py-2 px-4 bg-gray-50 border-t border-gray-100">
                  <div className="flex items-center text-xs font-medium text-[#363062]">
                    View Details
                    <FiArrowRight className="ml-1.5 w-3 h-3" />
                  </div>
                </div> */}
              </div>
            ))
          )}
        </div>
      </div>
    </MobileWrapper>
  );
}

export default InventoryIssuances;
